import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  Menu as MenuIcon,
  Settings,
  QrCode,
  Eye,
  LogOut,
  CreditCard,
  LifeBuoy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon } from "lucide-react";
import { APP_VERSION, BUILD_HASH } from "@/version";


export const AdminLayout = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);

  const isDashboard =
    pathname === '/admin' ||
    pathname === '/admin/';

  const mobilePageTitle =
    {
      '/admin/menu': 'İçerik',
      '/admin/banners': 'Kampanyalar',
      '/admin/preview': 'Vitrin Ön İzleme',
      '/admin/qr-code': 'QR Kod',
      '/admin/support': 'Destek',
      '/admin/subscription': 'Abonelik',
      '/admin/profile': 'Ayarlar',
    }[pathname] || 'Web Menü';

  const isMobileOptimizedPage =
    isDashboard ||
    pathname === '/admin/menu' ||
    pathname === '/admin/banners' ||
    pathname === '/admin/qr-code' ||
    pathname === '/admin/support' ||
    pathname === '/admin/subscription' ||
    pathname === '/admin/profile';

  const handleMobileBack = () => {
    const routerHistoryIndex = Number(window.history.state?.idx);

    if (Number.isFinite(routerHistoryIndex) && routerHistoryIndex > 0) {
      navigate(-1);
      return;
    }

    navigate('/admin', { replace: true });
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Genel Bakış' },
    { path: '/admin/menu', icon: MenuIcon, label: 'İçerik Yönetimi' },
    { label: "Kampanyalar", icon: ImageIcon, path: "/admin/banners" },
    { path: '/admin/preview', icon: Eye, label: 'Vitrin Ön İzleme' },
    { path: '/admin/qr-code', icon: QrCode, label: 'QR Kod & Bağlantı' },
    // 🆕 Abonelik / ödeme sayfası
    { path: '/admin/support', icon: LifeBuoy, label: 'Destek Talebi' },
    { path: '/admin/subscription', icon: CreditCard, label: 'Abonelik' },
    { path: '/admin/profile', icon: Settings, label: 'Ayarlar' },
  ];

  return (
    <div className="h-[100dvh] bg-[#FCFBF9] flex overflow-hidden md:h-auto md:min-h-screen md:overflow-x-clip md:overflow-y-visible">
      {/* 🟦 DESKTOP SIDEBAR */}
      <aside className="fixed bottom-0 left-0 top-0 z-30 hidden w-[272px] flex-col border-r border-zinc-200/80 bg-white md:flex">
        <div className="flex h-[88px] items-center border-b border-zinc-100 px-5">
          <Link to="/admin" className="flex min-w-0 items-center gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[15px] bg-[#EA580C] text-white shadow-[0_10px_28px_rgba(234,88,12,0.22)]">
              <MenuIcon className="h-5 w-5" />
            </div>

            <div className="min-w-0 leading-tight">
              <div className="truncate text-[15px] font-extrabold tracking-[-0.02em] text-zinc-950">
                Web Menü
              </div>
              <div className="mt-1 truncate text-[10px] font-bold uppercase tracking-[0.13em] text-zinc-400">
                Yönetim Paneli
              </div>
            </div>
          </Link>
        </div>

        <div className="px-5 pb-2 pt-6">
          <p className="px-3 text-[10px] font-extrabold uppercase tracking-[0.16em] text-zinc-400">
            Yönetim
          </p>
        </div>

        <nav className="flex-1 space-y-1.5 overflow-y-auto px-4 pb-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex min-h-11 items-center gap-3 rounded-[14px] px-3.5 py-3 text-sm font-semibold transition ${
                  isActive
                    ? 'bg-orange-50 text-[#C2410C]'
                    : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950'
                }`}
              >
                <span
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-[11px] transition ${
                    isActive
                      ? 'bg-[#EA580C] text-white shadow-sm'
                      : 'bg-zinc-100 text-zinc-500 group-hover:bg-white group-hover:text-zinc-900'
                  }`}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>

                <span className="min-w-0 truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="mx-4 mb-3 rounded-[18px] border border-orange-100 bg-orange-50/60 p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white text-[#EA580C] shadow-sm">
              <QrCode className="h-4 w-4" />
            </div>

            <div className="min-w-0">
              <p className="text-xs font-extrabold text-zinc-900">
                Dijital vitrininiz
              </p>
              <p className="mt-0.5 text-[11px] font-medium leading-4 text-zinc-500">
                İçeriğinizi güncel tutun, bağlantınızı paylaşın.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-100 p-4">
          <Button
            variant="ghost"
            className="min-h-11 w-full justify-start rounded-[14px] px-3 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
            onClick={logout}
          >
            <LogOut className="mr-3 h-[18px] w-[18px]" />
            Çıkış Yap
          </Button>

          <div className="mt-3 px-3 text-[10px] font-medium text-zinc-400">
            v{APP_VERSION} • {BUILD_HASH}
          </div>
        </div>
      </aside>

      {/* 🟧 MOBILE TOP BAR */}
      <header
        className={`${isDashboard ? "hidden" : "flex"} md:hidden fixed top-0 left-0 z-40 h-14 w-full items-center justify-center border-b border-zinc-200 bg-white px-4`}
      >
        <button
          type="button"
          onClick={handleMobileBack}
          aria-label="Önceki sayfaya dön"
          className="absolute left-2 flex min-h-11 items-center gap-1 rounded-lg px-2 text-[15px] font-medium text-[#18181B] active:bg-zinc-100"
        >
          <span className="-mt-0.5 text-[28px] font-light leading-none">‹</span>
          <span>Geri</span>
        </button>

        <span className="max-w-[58%] truncate text-[17px] font-semibold text-[#18181B]">
          {mobilePageTitle}
        </span>

        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Menüyü aç"
          aria-expanded={mobileOpen}
          className="absolute right-2 flex h-11 w-11 items-center justify-center rounded-lg text-[#18181B] active:bg-zinc-100"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
      </header>

      {/* 🟩 MOBILE SIDEBAR OVERLAY */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* 🟦 MOBILE SIDEBAR BODY */}
        <aside
          className={`fixed top-0 bottom-0 left-0 z-50 w-64 overflow-y-auto bg-white border-r border-zinc-200 flex-col transform transition-transform md:hidden ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
        <div className="flex items-center justify-between border-b border-zinc-100 p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-[14px] bg-[#EA580C] text-white">
              <MenuIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-extrabold text-zinc-950">Web Menü</div>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-400">
                Yönetim Paneli
              </div>
            </div>
          </div>
          <button
            className="flex h-11 w-11 items-center justify-center rounded-lg text-zinc-500 active:bg-zinc-100"
            onClick={() => setMobileOpen(false)}
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex min-h-11 items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-orange-50 text-[#C2410C]'
                    : 'text-zinc-700 hover:bg-zinc-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 pb-2 text-center">
          <div className="text-xs text-zinc-400">
            v{APP_VERSION} • {BUILD_HASH}
          </div>
        </div>

        <div className="p-4 border-t border-zinc-100">
          <Button
            variant="ghost"
            className="min-h-11 w-full justify-start text-zinc-700 hover:bg-zinc-100"
            onClick={logout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Dashboard native-like görünümünü korurken mobile menüyü erişilebilir tut */}
      {isDashboard && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Menüyü aç"
          aria-expanded={mobileOpen}
          className="fixed right-3 top-3 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 bg-white/95 text-[#18181B] shadow-sm backdrop-blur md:hidden"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
      )}

      {/* 🟣 MAIN CONTENT */}
      <main className="h-full min-h-0 flex-1 min-w-0 flex flex-col overflow-x-clip overflow-y-auto overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] md:h-auto md:min-h-screen md:overflow-y-visible md:overscroll-auto md:ml-[272px]">
        {/* mobile için üstteki barın boşluğu */}
        {!isDashboard && <div className="h-14 shrink-0 md:hidden" />}
        <div
          className={`w-full min-w-0 ${
            isMobileOptimizedPage ? "px-0 py-0" : "px-3 py-4"
          } md:px-8 md:py-8 xl:px-10`}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
